import React, { useEffect, useState } from 'react';
import { getApi } from '../utils/api';
import { Timestamp } from './ui/timestamp';

interface ImportHistoryProps {
  orgId: string;
  onViewDetails?: (jobId: string) => void;
}

const ImportHistory: React.FC<ImportHistoryProps> = ({ orgId, onViewDetails }) => {
  const [importJobs, setImportJobs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const api = getApi();

  const fetchImportJobs = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(`/v1/import/jobs?org_id=${orgId}&page=${page}`);
      setImportJobs(response.data.items);
      setTotalPages(response.data.pages);
    } catch (err: any) {
      console.error('Error fetching import jobs:', err);
      setError('Failed to load import history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchImportJobs();
  }, [orgId, page]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Completed</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">Failed</span>;
      case 'processing':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">Processing</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">{status}</span>;
    }
  };

  if (isLoading && page === 1) {
    return (
      <div className="flex justify-center p-4">
        <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-700">{error}</p>
        <button 
          className="mt-2 px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
          onClick={fetchImportJobs}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg">
      <div className="px-6 py-4 border-b">
        <h2 className="text-xl font-semibold">Import History</h2>
      </div>
      
      <div className="p-4">
        {importJobs.length === 0 ? (
          <p className="text-center text-gray-500 py-4">No import jobs found</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Filename
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {importJobs.map((job) => (
                    <tr key={job.import_job_id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <Timestamp value={job.created_at} format="absolute" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {job.filename}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(job.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {job.processed_count} / {job.total_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button 
                          className="text-blue-600 hover:text-blue-900"
                          onClick={() => onViewDetails && onViewDetails(job.import_job_id)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <button 
                  className="px-3 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setPage(p => Math.max(p - 1, 1))}
                  disabled={page === 1 || isLoading}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <button 
                  className="px-3 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ImportHistory;